import { HARDWARE_SOURCE_CONFIGS, HardwareSourceConfig } from "src/utils/ConfigConstant";

interface Attribute {
    name: string,
    value: string
}

interface DisableableAttribute {
    name: string,
    value: boolean,
    disabled: boolean
}

type AttributeMap = {[key: string]: Attribute[]};
type DisableableAttributeMap = {[key: string]: DisableableAttribute[]};

const REGION_TO_SUPPORTED_HARDWARE: Map<string, string[]> = getRegionToSupportedHardware(HARDWARE_SOURCE_CONFIGS);

// Debug logging to see what data we have
console.log('=== REGION_TO_SUPPORTED_HARDWARE Debug ===');
console.log('Full REGION_TO_SUPPORTED_HARDWARE Map:', REGION_TO_SUPPORTED_HARDWARE);
REGION_TO_SUPPORTED_HARDWARE.forEach((sources, region) => {
    console.log(`Region ${region}:`, sources);
});
console.log('=== End REGION_TO_SUPPORTED_HARDWARE Debug ===');

export const REGION_HARDWARE_SOURCE_MAP: AttributeMap = {
    NA: [
        { name: "ALL", value: "ALL" },
        ...getHardwareSourceNamesAndValuesFromRegion(REGION_TO_SUPPORTED_HARDWARE, 'IAD')
    ],
    EU: [
        { name: "ALL", value: "ALL" },
        ...getHardwareSourceNamesAndValuesFromRegion(REGION_TO_SUPPORTED_HARDWARE, 'DUB')
    ],

    JP: [
        { name: "ALL", value: "ALL" },
        ...getHardwareSourceNamesAndValuesFromRegion(REGION_TO_SUPPORTED_HARDWARE, 'NRT')
    ]
}

export const REGION_DROPDOWN = [
    { name: "NA", value: "NA" },
    { name: "EU", value: "EU" },
    { name: "JP", value: "JP" }
]

export const ITEM_ID_TYPE_DROPDOWN = [
    { name: "ASIN", value: "ASIN" },
    { name: "FNSKU", value: "FNSKU" },
    { name: "FCSKU", value: "FCSKU" }
]

export const HARDWARE_SOURCE_TO_SENSOR_TYPE_MAP: AttributeMap = {
    ALL: [
        { name: "ALL", value: "ALL" }
    ],
    ...getHardwareToSensorAttributes(HARDWARE_SOURCE_CONFIGS)
}

export const HARDWARE_SOURCE_SET_SUPPORT_INCOMPLETE_CAPTURE: string[] = getIncompleteHardwareSources(HARDWARE_SOURCE_CONFIGS);

export const HARDWARE_SOURCE_TO_INCOMPLETE_CAPTURE_MAP: DisableableAttributeMap = {
    ALL: createIncompleteCaptureAttributes(true),
    ...createHardwareSourceToIncompleteCaptureAttributes(HARDWARE_SOURCE_CONFIGS)
}

function getRegionToSupportedHardware(hardwareSourceConfigs: Map<string, HardwareSourceConfig>): Map<string, string[]> {
    const regionToHardwareSources: Map<string, string[]> = new Map<string, string[]>([
        ["IAD", []],
        ["DUB", []],
        ["NRT", []],
        ["Gamma", []],
    ]);

    hardwareSourceConfigs.forEach((config: HardwareSourceConfig, hardwareSource: string) => {
        config.supportedRegions.forEach(region => {
            regionToHardwareSources.get(region)!.push(hardwareSource);
        });
    });
    return regionToHardwareSources;
}

function getHardwareSourceNamesAndValuesFromRegion(regionToSupportedHardware: Map<string, string[]>, region: string): Attribute[] {
    const sources = regionToSupportedHardware.get(region) || [];
    const result = sources.map(hardwareSource => {
        return { name: hardwareSource, value: hardwareSource }
    });
    
    // Debug logging
    console.log(`=== getHardwareSourceNamesAndValuesFromRegion Debug for ${region} ===`);
    console.log(`Raw sources for ${region}:`, sources);
    console.log(`Mapped result for ${region}:`, result);
    console.log(`=== End getHardwareSourceNamesAndValuesFromRegion Debug for ${region} ===`);
    
    return result;
}

function getSensorAttribute(sensor: string): Attribute {
    return { name: sensor.replace(new RegExp('-', 'g'), '_'), value: sensor };
}

function getSensorAttributesForHardware(hardwareSourceConfig: HardwareSourceConfig): Attribute[] {
    return [
        {name: 'ALL', value: 'ALL'},
        ...hardwareSourceConfig.sensorTypes.map(sensor => getSensorAttribute(sensor))
    ];
}

function getHardwareToSensorAttributes(hardwareSourceConfigs: Map<string, HardwareSourceConfig>): AttributeMap {
    const hardwareToSensorAttributes: AttributeMap = {};

    hardwareSourceConfigs.forEach((config: HardwareSourceConfig, hardwareSource: string) => {
        hardwareToSensorAttributes[hardwareSource] = getSensorAttributesForHardware(config);
    });

    return hardwareToSensorAttributes;
}

function getIncompleteHardwareSources(hardwareSourceConfigs: Map<string, HardwareSourceConfig>): string[] {
    const incompleteHardwareSources: string[] = [];

    hardwareSourceConfigs.forEach((config: HardwareSourceConfig, hardwareSource: string) => {
        if (config.supportIncompleteCapture) {
            incompleteHardwareSources.push(hardwareSource);
        }
    });

    return incompleteHardwareSources;
}

function createIncompleteCaptureAttributes(supportIncompleteCapture: boolean): DisableableAttribute[] {
    return [
        {name: "True", value: true, disabled: !supportIncompleteCapture},
        {name: "False", value: false, disabled: false}
    ]
}

function createHardwareSourceToIncompleteCaptureAttributes(hardwareSourceConfigs: Map<string, HardwareSourceConfig>): DisableableAttributeMap {
    const hardwareSourceToIncompleteCaptureAttributes: DisableableAttributeMap = {};

    hardwareSourceConfigs.forEach((config: HardwareSourceConfig, hardwareSource: string) => {
        hardwareSourceToIncompleteCaptureAttributes[hardwareSource] = createIncompleteCaptureAttributes(config.supportIncompleteCapture);
    });

    return hardwareSourceToIncompleteCaptureAttributes;
}
